import { IconButton, Tooltip } from "@mui/material";
import { useRef, useEffect } from "react";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import ImageIcon from "@mui/icons-material/Image";
import SaveAltTwoToneIcon from "@mui/icons-material/SaveAltTwoTone";
import PdfViewer from "./Pdfviewer";
import ExcelViewer from "./ExcelViewer";
import VideoViewer from "./VideoViewer";
import ImageViewer from "./ImageViewer";

const getExt = (str) => {
  const match = str && str.match(/\.([a-z0-9]+)(\?.*)?$/i);
  return match ? match[1] : "";
};

const FileItem = ({ file, color, playingId, setPlayingId, getIconOnly }) => {
  const lref = useRef();
  const audioRef = useRef();

  // Get filename and url
  const filename = (file.filename || file.file_name || "").toLowerCase();
  const fileurl = (file.surl || file.url || file.cdn_url || file.turl || "")?.toLowerCase();
  const ext = getExt(filename) || getExt(fileurl);

  // Audio file extensions
  const isAudio = ext.match(/(mp3|wav|ogg|aac)$/);

  // Unique id for this audio (use file.id or file.surl as fallback)
  const audioId = file.id || file.surl || file.url || file.cdn_url || file.turl;

  // Always call hooks at the top level!
  useEffect(() => {
    if (audioRef.current && playingId !== audioId) {
      audioRef.current.audio.current.pause();
    }
  }, [playingId, audioId]);

  // File icon or preview
  const getFileIcon = () => {
    if (file.mtyp === "I" || ext.match(/(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return <ImageIcon color="primary" />;
    }
    if (ext.match(/pdf$/)) return <PictureAsPdfIcon color="error" />;
if (isAudio) return <AudiotrackIcon color="success" />;
if (ext.match(/(mp4|avi|mov|wmv|flv|webm)$/)) return <VideoFileIcon color="secondary" />;
if (ext.match(/(xls|xlsx|csv)$/)) return <TableChartIcon color="info" />;
if (ext.match(/(doc|docx)$/)) return <DescriptionIcon color="primary" />;
return <InsertDriveFileIcon />;

  };

  // Only render icon if getIconOnly is true
  if (getIconOnly) {
    return getFileIcon();
  }

  if (isAudio) {
    // Always prefer cdn_url for download and playback
    const src = file.cdn_url || file.surl || file.url || file.turl;

    const onClickDownload = () => {
      const a = document.createElement("a");
      a.href = src; // src is your CDN url
      a.download = src.split("/").pop();
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    // Custom Download Button for AudioPlayer
    const DownloadBtn = (
      <Tooltip title="Download" arrow>
        <IconButton
          size="small"
          onClick={onClickDownload}
          sx={{
            fontSize: "0.70rem",
            borderRadius: 2,
            color: "white",
            minHeight: 28,
            minWidth: 28,
            padding: "2px 8px",
            backgroundColor: "primary.main",
            "&:hover": { backgroundColor: "primary.dark" },
            display: "flex",
            alignItems: "center",
          }}
        >
          <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
          <span style={{ fontSize: "0.70rem" }}>Download</span>
        </IconButton>
      </Tooltip>
    );

    return (
      <Tooltip title={filename || "File"} arrow>
        <span style={{ display: "block", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AudioPlayer
              ref={audioRef}
              src={src}
              volume={0.5}
              style={{
                borderRadius: 8,
                width: "100%",
                minWidth: 250,
                maxWidth: 500,
                margin: "0 auto",
              }}
              showJumpControls={true}
              customAdditionalControls={[DownloadBtn]}
              customVolumeControls={["VOLUME"]}
              onPlay={() => setPlayingId && setPlayingId(audioId)}
            />
          </div>
        </span>
      </Tooltip>
    );
  }

const isPDF = ext.match(/pdf$/);
if (isPDF) {
  const pdfUrl = file.cdn_url || file.surl || file.url || file.turl;
  if (!pdfUrl) {
    return (
      <Tooltip title={filename || "File"} arrow>
        <span>
          <div style={{ marginTop: 8, color: "red" }}>
            PDF file not available.
          </div>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={filename || "File"} arrow>
      <span>
        <div style={{ marginTop: 8 }}>
      <PdfViewer pdffile={{ surl: pdfUrl }} />
        </div>
      </span>
    </Tooltip>
  );
}

  const isExcel = ext.match(/(xls|xlsx|csv)$/);

  if (isExcel) {
    return (
      <Tooltip title={filename || "File"} arrow>
        <span>
          <div style={{ marginTop: 8 }}>
            <ExcelViewer excelfile={file} />
          </div>
        </span>
      </Tooltip>
    );
  }

  const isVideo = ext.match(/(mp4|avi|mov|wmv|flv|webm)$/);

  if (isVideo) {
    return (
      <Tooltip title={filename || "File"} arrow>
        <span>
          <div style={{ marginTop: 8 }}>
            <VideoViewer videofile={file} />
          </div>
        </span>
      </Tooltip>
    );
  }

  const isImage = file.mtyp === "I" || ext.match(/(jpg|jpeg|png|gif|bmp|webp)$/);

  if (isImage) {
    return (
      <Tooltip title={filename || "File"} arrow>
        <span>
          <div style={{ marginTop: 8 }}>
            <ImageViewer imagefile={file} />
          </div>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={filename || "File"} arrow>
      <span>
        <IconButton onClick={() => lref.current.click()}>
          {getFileIcon()}
          <a
            ref={lref}
            style={{ display: "none" }}
            href={file.surl}
            target="_blank"
            rel="noreferrer"
            download={file.surl ? file.surl.split("/").pop() : ""}
          >
            File
          </a>
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default FileItem;
